import React, { useRef, useState } from 'react'
import DossierRecord from 'dossiers/domain/DossierRecord'
import MarkdownAndHtmlToHtml from 'common/utils/MarkdownAndHtmlToHtml'
import withData from 'http/withData'
import DossiersService from 'dossiers/application/DossiersService'
import _ from 'lodash'
import { Popover, Overlay } from 'react-bootstrap'
import { Fragment } from 'fragmentarium/domain/fragment'
import 'dossiers/ui/DossiersDisplay.sass'
import ReferencePopover from 'bibliography/ui/referencePopover'
import Citation from 'bibliography/domain/Citation'
import InlineMarkdown from 'common/ui/InlineMarkdown'
import Reference from 'bibliography/domain/Reference'

function ReferencesDisplay({
  references,
}: {
  references: Reference[]
}): JSX.Element {
  const InlineMarkdownWithPopover = ReferencePopover(
    ({ reference }: { reference: Reference }) => (
      <InlineMarkdown source={Citation.for(reference).getMarkdown()} />
    ),
  )
  return (
    <>
      <strong>Bibliography</strong>:{' '}
      {references.map((reference, index) => (
        <React.Fragment key={index}>
          {index > 0 && '; '}
          <InlineMarkdownWithPopover reference={reference} />
        </React.Fragment>
      ))}
    </>
  )
}

export function DossierRecordDisplay({
  record,
  index,
}: {
  record: DossierRecord
  index: number
}): JSX.Element {
  return (
    <>
      <MarkdownAndHtmlToHtml
        key={`dossier-md-${index}`}
        markdownAndHtml={record.toMarkdownString({ bibliography: false })}
        className="dossier-record"
      />
      <ReferencesDisplay references={record.references} />
    </>
  )
}

function DossierInfoSpan({
  index,
  isActive,
  setActiveDossier,
  targetRef,
  recordId,
}: {
  index: number
  isActive: boolean
  setActiveDossier: React.Dispatch<React.SetStateAction<number | null>>
  targetRef: React.RefObject<HTMLButtonElement>
  recordId: string
}): JSX.Element {
  const popoverId = `DossierReferencePopOver-${index}`

  return (
    <span
      key={`dossier-span-${index}`}
      className={`dossier-records__item${isActive ? '__active' : ''}`}
    >
      {index === 0 && <span className="dossier-prefix">Dossier: </span>}
      {index > 0 && '| '}
      <button
        ref={targetRef}
        type="button"
        className="dossier-name"
        onClick={() => setActiveDossier(isActive ? null : index)}
        aria-expanded={isActive}
        aria-controls={popoverId}
      >
        {recordId}
      </button>
    </span>
  )
}

function DossierPopover({
  index,
  isActive,
  targetRef,
  setActiveDossier,
  record,
}: {
  index: number
  isActive: boolean
  targetRef: React.RefObject<HTMLButtonElement>
  setActiveDossier: React.Dispatch<React.SetStateAction<number | null>>
  record: DossierRecord
}): JSX.Element {
  return (
    <Overlay
      key={`dossier-overlay-${index} fade`}
      target={targetRef.current}
      placement="right"
      show={isActive}
      onEnter={() => setActiveDossier(index)}
      onHide={() => setActiveDossier(null)}
      rootClose={true}
      rootCloseEvent={'click'}
    >
      <Popover
        id={`DossierReferencePopOver-${index}`}
        className="reference-popover__popover"
      >
        <Popover.Header as="h3">{record.id}</Popover.Header>
        <Popover.Body>
          <DossierRecordDisplay record={record} index={index} />
        </Popover.Body>
      </Popover>
    </Overlay>
  )
}

function DossierRecordItem(props: {
  record: DossierRecord
  index: number
  activeDossier: number | null
  setActiveDossier: React.Dispatch<React.SetStateAction<number | null>>
}): JSX.Element {
  const { record, index, activeDossier, setActiveDossier } = props
  const target = useRef<HTMLButtonElement>(null)
  const isActive = activeDossier === index

  return (
    <>
      <DossierInfoSpan
        index={index}
        isActive={isActive}
        setActiveDossier={setActiveDossier}
        targetRef={target}
        recordId={record.id}
      />
      <DossierPopover
        index={index}
        isActive={isActive}
        targetRef={target}
        setActiveDossier={setActiveDossier}
        record={record}
      />
    </>
  )
}

export function DossierRecordsListDisplay({
  data,
}: {
  data: { records: readonly DossierRecord[] }
} & React.OlHTMLAttributes<HTMLOListElement>): JSX.Element {
  const { records } = data
  const [activeDossier, setActiveDossier] = useState<number | null>(null)

  if (records.length < 1) {
    return <></>
  }
  return (
    <>
      {records.map((record, index) => (
        <DossierRecordItem
          key={index}
          {...{ index, record, activeDossier, setActiveDossier }}
        />
      ))}
    </>
  )
}

const FragmentDossierRecordsDisplay = withData<
  unknown,
  {
    dossiersService: Pick<DossiersService, 'queryByIds'>
    fragment: Fragment
  },
  { records: readonly DossierRecord[] }
>(
  DossierRecordsListDisplay,
  (props) => {
    return Promise.resolve(
      props.dossiersService
        .queryByIds([
          ...props.fragment.dossiers.map((record) => record.dossierId),
        ])
        .then((records) => ({ records })),
    )
  },
  {
    watch: (props) => [
      props.fragment.dossiers.map((dossier) => dossier.dossierId).join('|'),
    ],
    filter: (props) => !_.isEmpty(props.fragment.dossiers),
    defaultData: () => ({ records: [] }),
  },
)

export default FragmentDossierRecordsDisplay
